import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomerService } from '../../../../customer.service';
import { CustomerSharedService } from '../../../../customer-shared.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ActivatedRoute } from '@angular/router';

@UntilDestroy({ checkProperties: true })

@Component({
  selector: 'app-checklist-master',
  templateUrl: './checklist-master.component.html',
  styleUrls: ['./checklist-master.component.css']
})
export class ChecklistMasterComponent implements OnInit {
  isAddChecklistVisible: boolean = false;
  addChecklistForm: FormGroup;
  mode: 'create' | 'append' | 'edit' = 'create';
  selectedChecklistName: string | null = null;
  selectedRowIndex: number | null = null;
  id!: number | string;

  readonly FILE_TYPES: string[] = ['PDF', 'DOCX', 'XLSX', 'JPG', 'PNG'];
  readonly TRIGGER_EVENT_OPTIONS: string[] = ['Start trip', 'End trip', 'Start inventory movement', 'End inventory movement'];

  checklists: Array<{
    name: string;
    rows: Array<{
      title: string;
      triggerEvent: string[];
      mandatory: boolean;
      maxAttachments: number;
      allowedFileType: string[];
      maxSizeMb: number;
    }>;
  }> = [];

  constructor(private formBuilder: FormBuilder, private customerService: CustomerService, private customerSharedService: CustomerSharedService, private commonSvc: CommonService, private acRoute: ActivatedRoute) {
    const id = this.acRoute.snapshot.paramMap.get('id');
    if (id) this.id = id;
    this.addChecklistForm = this.formBuilder.group({
      checklist_name: [null, [Validators.required, Validators.maxLength(50)]],
      trigger_event: [[]],
      title: [null, [Validators.required, Validators.maxLength(100)]],
      mandatory: [true, Validators.required],
      max_attachments: [null, [Validators.required, Validators.pattern('^\\d+$')]],
      allowed_file_type: [[], Validators.required],
      max_size_mb: [null, [Validators.required, Validators.pattern('^\\d+$')]]
    });
    this.addChecklistForm.get('allowed_file_type')?.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((val: string[] | null) => {
        if (!val) return;
        if (val.includes('ALL')) {
          this.addChecklistForm.get('allowed_file_type')?.setValue([...this.FILE_TYPES], { emitEvent: false });
        }
      });
  }

  ngOnInit(): void {
    this.loadExistingChecklistData();
  }

  loadExistingChecklistData(): void {
    if (!this.id) return;

    this.customerService.getCustomerById(this.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response: any) => {
          if (response?.status === 200 && response?.data) {
            // Handle both array and object response structures
            const customerData = Array.isArray(response.data)
              ? response.data[0]
              : response.data;

            if (customerData?.checklist) {
              this.parseChecklistData(customerData.checklist);
            }
          }
        },
        error: (err: any) => {
          console.error('Failed to load checklist data', err);
        }
      });
  }

  parseChecklistData(checklistData: any): void {
    this.checklists = [];
    Object.keys(checklistData).forEach(checklistName => {
      const rowsData = checklistData[checklistName];
      const rows = Array.isArray(rowsData) ? rowsData.map((r: any) => ({
        title: r.title || '',
        triggerEvent: Array.isArray(r.triggerEvent) ? r.triggerEvent : [],
        mandatory: !!r.mandatory,
        maxAttachments: Number(r.maxAttachments) || 0,
        allowedFileType: Array.isArray(r.allowedFileType) ? r.allowedFileType : [],
        maxSizeMb: Number(r.maxSizeMb) || 0
      })) : [];

      if (rows.length > 0) {
        this.checklists.push({ name: checklistName, rows });
      }
    });
    this.updateSharedChecklistPayload();
  }

  openAddChecklist(): void {
    this.mode = 'create';
    this.selectedChecklistName = null;
    this.addChecklistForm.reset();
    this.addChecklistForm.get('checklist_name')?.enable();
    this.isAddChecklistVisible = true;
  }

  isGroupNameDisabled(groupName: string): boolean {
    return this.checklists.some(cl => cl.name === groupName);
  }

  handleAddChecklistCancel(): void {
    this.isAddChecklistVisible = false;
    this.addChecklistForm.reset();
  }

  submitAddChecklist(): void {
    if (this.addChecklistForm.invalid) {
      this.addChecklistForm.markAllAsTouched();
      Object.values(this.addChecklistForm.controls).forEach(ctrl => ctrl.updateValueAndValidity());
      this.commonSvc.message.error('Please fill all required fields');
      return;
    }
    const formValue = this.addChecklistForm.value;
    const checklistNameKey: string = formValue.checklist_name;
    const row = {
      title: formValue.title,
      triggerEvent: (formValue.trigger_event || []) as string[],
      mandatory: !!formValue.mandatory,
      maxAttachments: Number(formValue.max_attachments),
      allowedFileType: (formValue.allowed_file_type || []) as string[],
      maxSizeMb: Number(formValue.max_size_mb)
    };

    if (this.mode === 'create') {
      // Create a new checklist card with the first row
      const newChecklist = { name: checklistNameKey, rows: [row] };
      this.checklists.push(newChecklist);
      this.updateSharedChecklistPayload();
    } else if (this.mode === 'append') {
      // Append a row to existing checklist
      const targetName = this.selectedChecklistName as string;
      const target = this.checklists.find(c => c.name === targetName);
      if (target) {
        target.rows.push(row);
        this.updateSharedChecklistPayload();
      }
    } else if (this.mode === 'edit') {
      const targetName = this.selectedChecklistName as string;
      const target = this.checklists.find(c => c.name === targetName);
      if (target && this.selectedRowIndex !== null) {
        const original = target.rows[this.selectedRowIndex];
        // Update only changed fields
        const updated = { ...original };
        if (row.title !== original.title) updated.title = row.title;
        const sameTrigger = JSON.stringify(row.triggerEvent) === JSON.stringify(original.triggerEvent);
        if (!sameTrigger) updated.triggerEvent = row.triggerEvent;
        if (row.mandatory !== original.mandatory) updated.mandatory = row.mandatory;
        if (row.maxAttachments !== original.maxAttachments) updated.maxAttachments = row.maxAttachments;
        const sameTypes = JSON.stringify(row.allowedFileType) === JSON.stringify(original.allowedFileType);
        if (!sameTypes) updated.allowedFileType = row.allowedFileType;
        if (row.maxSizeMb !== original.maxSizeMb) updated.maxSizeMb = row.maxSizeMb;

        target.rows[this.selectedRowIndex] = { ...original, ...updated };
        this.updateSharedChecklistPayload();
      }
    }

    this.isAddChecklistVisible = false;
    this.addChecklistForm.reset();
  }

  openAddRowFor(checklistName: string): void {
    this.mode = 'append';
    this.selectedChecklistName = checklistName;
    this.addChecklistForm.reset({ checklist_name: checklistName });
    this.addChecklistForm.get('checklist_name')?.disable();
    this.isAddChecklistVisible = true;
  }

  deleteRow(checklistName: string, rowIndex: number): void {
    const target = this.checklists.find(c => c.name === checklistName);
    if (!target) return;
    // (Removed restriction: allow deleting the last remaining row)
    target.rows.splice(rowIndex, 1);
    this.updateSharedChecklistPayload();
  }

  openEditRow(checklistName: string, rowIndex: number): void {
    this.mode = 'edit';
    this.selectedChecklistName = checklistName;
    this.selectedRowIndex = rowIndex;
    const target = this.checklists.find(c => c.name === checklistName);
    if (!target) return;
    const row = target.rows[rowIndex];
    this.addChecklistForm.reset({
      checklist_name: checklistName,
      trigger_event: [...row.triggerEvent],
      title: row.title,
      mandatory: row.mandatory,
      max_attachments: row.maxAttachments,
      allowed_file_type: [...row.allowedFileType],
      max_size_mb: row.maxSizeMb
    });
    this.addChecklistForm.get('checklist_name')?.disable();
    this.isAddChecklistVisible = true;
  }

  private updateSharedChecklistPayload(): void {
    const payload: Record<string, any[]> = {};
    this.checklists.forEach(cl => {
      payload[cl.name] = cl.rows.map(r => ({
        title: r.title,
        triggerEvent: r.triggerEvent,
        mandatory: r.mandatory,
        maxAttachments: r.maxAttachments,
        allowedFileType: r.allowedFileType,
        maxSizeMb: r.maxSizeMb
      }));
    });
    this.customerSharedService.setChecklistConfig(payload);
  }

  submitChanges(): void {
    const payload: Record<string, any[]> = {};
    this.checklists.forEach(cl => {
      payload[cl.name] = cl.rows.map(r => ({
        title: r.title,
        triggerEvent: r.triggerEvent,
        mandatory: r.mandatory,
        maxAttachments: r.maxAttachments,
        allowedFileType: r.allowedFileType,
        maxSizeMb: r.maxSizeMb
      }));
    });


    if (!this.id) return;
    this.customerService.updateCustomer(this.id, { checklist: payload }).subscribe({
      next: (res: any) => {
        if (res.status === 200) {
          this.commonSvc.message.success('Checklist updated successfully');
          console.log('Success response:', res);
        }
      },
      error: (err: any) => {
        this.commonSvc.message.error('Failed to update checklist');
        console.error('Error updating checklist:', err);
      }
    });
  }
}


